import React, { useEffect, useState, useCallback } from 'react';
import { Package, Search } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/contexts/AuthContext';
import Layout from '@/components/Layout';
import { getDealerOrders } from '@/lib/api';

const OrdersPage = () => {
  const { dealer } = useAuth();
  const [orders, setOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchOrders = useCallback(async () => {
    if (!dealer) return;
    try {
      const res = await getDealerOrders(dealer.id);
      setOrders(res.data || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  }, [dealer]);

  useEffect(() => {
    if (dealer) {
      fetchOrders();
    }
  }, [dealer, fetchOrders]);

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      confirmed: 'bg-green-100 text-green-800 border-green-200',
      paid: 'bg-blue-100 text-blue-800 border-blue-200'
    };

    return (
      <Badge variant="outline" className={variants[status] || ''}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const filteredOrders = orders.filter(order =>
    order.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.order_id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" data-testid="orders-title">Orders</h1>
          <p className="text-slate-600 mt-1">Track all your referred orders</p>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            data-testid="search-orders-input"
            placeholder="Search by customer name or order ID"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Orders List */}
        <Card data-testid="orders-list-card">
          <CardHeader>
            <CardTitle>All Orders</CardTitle>
            <CardDescription>Commission is 50% of first month rent</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-slate-500">Loading orders...</div>
            ) : filteredOrders.length === 0 ? (
              <div className="text-center py-12">
                <Package className="mx-auto h-12 w-12 text-slate-300" />
                <h3 className="mt-4 text-lg font-medium text-slate-900">No orders yet</h3>
                <p className="mt-2 text-sm text-slate-500">
                  Start sharing your referral link to earn commissions
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-900">Order ID</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-900">Customer</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-slate-900">Order Amount</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-slate-900">Commission</th>
                      <th className="text-center py-3 px-4 text-sm font-semibold text-slate-900">Status</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-900">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrders.map((order) => (
                      <tr key={order.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid="order-row">
                        <td className="py-3 px-4 text-sm font-mono text-slate-700">{order.order_id}</td>
                        <td className="py-3 px-4 text-sm text-slate-900">{order.customer_name}</td>
                        <td className="py-3 px-4 text-sm text-right text-slate-900">₹{order.order_amount?.toLocaleString()}</td>
                        <td className="py-3 px-4 text-sm text-right font-semibold text-green-600">₹{order.commission_amount?.toLocaleString()}</td>
                        <td className="py-3 px-4 text-center">{getStatusBadge(order.status)}</td>
                        <td className="py-3 px-4 text-sm text-slate-600">
                          {new Date(order.created_at).toLocaleDateString('en-IN')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default OrdersPage;
